import React, { useEffect, useMemo, useState } from 'react';
import serviceTypeService from '../../services/serviceTypeService';
import serviceService from '../../services/serviceService';
import './dataEditor.css';

const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
const deepClone = (v) => {
  try {
    return JSON.parse(JSON.stringify(v));
  } catch (_) {
    return {};
  }
};

// Service List Editor Component - displays services for a service type
const ServiceListEditor = ({ serviceTypeId, services, onServicesChange }) => {
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');

  // Filter services by serviceType
  useEffect(() => {
    if (!serviceTypeId || !Array.isArray(services)) {
      setFilteredServices([]);
      return;
    }
    const filtered = services.filter(s => String(s.serviceType || '') === String(serviceTypeId));
    setFilteredServices(filtered);
  }, [serviceTypeId, services]);

  const handleCreateService = async () => {
    try {
      // Generate a new ID
      const maxId = services.reduce((max, item) => {
        const itemId = parseInt(item.id) || 0;
        return itemId > max ? itemId : max;
      }, 0);
      const newId = String(maxId + 1);

      // Create new service with the current serviceType
      const newService = {
        id: newId,
        serviceType: serviceTypeId,
        label: '',
        toURL: `/${serviceTypeId}/${newId}`,
        content: {
          hero: {},
          summary: {},
          sections: []
        },
        features: []
      };

      await serviceService.insertObjectToArrayData(newService);
      await onServicesChange();
      
      // Select the newly created service
      setSelectedServiceId(newId);
    } catch (error) {
      console.error('Failed to create service:', error);
      alert('創建服務失敗');
    }
  };

  const handleUpdateService = async (serviceId, updatedService) => {
    try {
      await serviceService.updateListObjectDataById(serviceId, updatedService);
      await onServicesChange();
    } catch (error) {
      console.error('Failed to update service:', error);
      alert('更新服務失敗');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('確定要刪除這個服務嗎？此操作無法撤銷。')) {
      return;
    }

    try {
      await serviceService.deleteObjectInArrayData(serviceId);
      await onServicesChange();
      
      // Clear selection if deleted service was selected
      if (selectedServiceId === serviceId) {
        setSelectedServiceId('');
      }
    } catch (error) {
      console.error('Failed to delete service:', error);
      alert('刪除服務失敗');
    }
  };

  const selectedService = filteredServices.find(s => String(s.id) === selectedServiceId);

  return (
    <div className="w-100">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">服務列表</h5>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={handleCreateService}
        >
          + 新增服務
        </button>
      </div>

      {filteredServices.length === 0 ? (
        <div className="text-muted">此服務類型下暫無服務</div>
      ) : (
        <>
          <div className="mb-3">
            <label className="form-label">選擇服務以編輯：</label>
            <select
              className="form-select form-select-sm"
              value={selectedServiceId}
              onChange={(e) => setSelectedServiceId(e.target.value)}
            >
              <option value="">-- 請選擇服務 --</option>
              {filteredServices.map(s => (
                <option key={s.id} value={s.id}>
                  {s.id} – {s.label || ''}
                </option>
              ))}
            </select>
          </div>

          {selectedService && (
            <ServiceEditor
              service={selectedService}
              onUpdate={(updated) => handleUpdateService(selectedServiceId, updated)}
              onDelete={() => handleDeleteService(selectedServiceId)}
            />
          )}
        </>
      )}
    </div>
  );
};

// Simple Service Editor Component
const ServiceEditor = ({ service, onUpdate, onDelete }) => {
  const [editedService, setEditedService] = useState(deepClone(service));
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'hero', 'summary', 'features', 'processes'

  useEffect(() => {
    setEditedService(deepClone(service));
    setHasChanges(false);
  }, [service]);

  useEffect(() => {
    const changed = JSON.stringify(editedService) !== JSON.stringify(service);
    setHasChanges(changed);
  }, [editedService, service]);

  const handleSave = () => {
    onUpdate(editedService);
  };

  const updateField = (field, value) => {
    setEditedService(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field, value) => {
    setEditedService(prev => ({ ...prev, [field]: value }));
  };

  const updateFeature = (index, value) => {
    const features = Array.isArray(editedService.features) ? editedService.features : [];
    const newFeatures = [...features];
    newFeatures[index] = value;
    updateArrayField('features', newFeatures);
  };

  const addFeature = () => {
    const features = Array.isArray(editedService.features) ? editedService.features : [];
    updateArrayField('features', [...features, '']);
  };

  const removeFeature = (index) => {
    const features = Array.isArray(editedService.features) ? editedService.features : [];
    const newFeatures = features.filter((_, i) => i !== index);
    updateArrayField('features', newFeatures);
  };

  const updateProcessField = (processKey, field, value) => {
    setEditedService(prev => {
      const newService = deepClone(prev);
      if (!newService.processes) {
        newService.processes = {};
      }
      if (!newService.processes[processKey]) {
        newService.processes[processKey] = { title: '', data: [] };
      }
      newService.processes[processKey][field] = value;
      return newService;
    });
  };

  const updateProcessDataItem = (processKey, itemIndex, field, value) => {
    setEditedService(prev => {
      const newService = deepClone(prev);
      if (!newService.processes) {
        newService.processes = {};
      }
      if (!newService.processes[processKey]) {
        newService.processes[processKey] = { title: '', data: [] };
      }
      const data = Array.isArray(newService.processes[processKey].data) ? newService.processes[processKey].data : [];
      const newData = [...data];
      if (!newData[itemIndex]) {
        newData[itemIndex] = { title: '', description: '' };
      }
      newData[itemIndex] = { ...newData[itemIndex], [field]: value };
      newService.processes[processKey].data = newData;
      return newService;
    });
  };

  const addProcessDataItem = (processKey) => {
    setEditedService(prev => {
      const newService = deepClone(prev);
      if (!newService.processes) {
        newService.processes = {};
      }
      if (!newService.processes[processKey]) {
        newService.processes[processKey] = { title: '', data: [] };
      }
      const data = Array.isArray(newService.processes[processKey].data) ? newService.processes[processKey].data : [];
      newService.processes[processKey].data = [...data, { title: '', description: '' }];
      return newService;
    });
  };

  const removeProcessDataItem = (processKey, itemIndex) => {
    setEditedService(prev => {
      const newService = deepClone(prev);
      if (!newService.processes || !newService.processes[processKey]) {
        return newService;
      }
      const data = Array.isArray(newService.processes[processKey].data) ? newService.processes[processKey].data : [];
      newService.processes[processKey].data = data.filter((_, i) => i !== itemIndex);
      return newService;
    });
  };

  return (
    <div className="card shadow-sm mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">編輯服務：{service.id}</h6>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={onDelete}
          >
            刪除服務
          </button>
        </div>

        {/* Tab Navigation */}
        <ul className="nav nav-tabs mb-3" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
              type="button"
            >
              基本資訊
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'hero' ? 'active' : ''}`}
              onClick={() => setActiveTab('hero')}
              type="button"
            >
              Hero
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
              type="button"
            >
              Summary
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'features' ? 'active' : ''}`}
              onClick={() => setActiveTab('features')}
              type="button"
            >
              特性
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'processes' ? 'active' : ''}`}
              onClick={() => setActiveTab('processes')}
              type="button"
            >
              Processes
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        {activeTab === 'basic' && (
          <div className="w-100">
            <div className="mb-2">
              <label className="form-label small">ID</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.id || ''}
                readOnly
                disabled
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">服務類型 (ServiceType)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.serviceType || ''}
                readOnly
                disabled
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">標籤 (Label)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.label || ''}
                onChange={(e) => updateField('label', e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">URL路徑 (toURL)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.toURL || ''}
                onChange={(e) => updateField('toURL', e.target.value)}
              />
            </div>
          </div>
        )}

        {activeTab === 'hero' && (
          <div className="w-100">
            <div className="mb-2">
              <label className="form-label small">Hero 標題 (heroTitle)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.heroTitle || ''}
                onChange={(e) => updateField('heroTitle', e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">Hero 副標題 (heroSubtitle)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.heroSubtitle || ''}
                onChange={(e) => updateField('heroSubtitle', e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">Hero 圖片URL (heroImageUrl)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.heroImageUrl || ''}
                onChange={(e) => updateField('heroImageUrl', e.target.value)}
              />
            </div>
          </div>
        )}

        {activeTab === 'summary' && (
          <div className="w-100">
            <div className="mb-2">
              <label className="form-label small">Summary 標題 (summaryTitle)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.summaryTitle || ''}
                onChange={(e) => updateField('summaryTitle', e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">Summary 內容 (summaryContent)</label>
              <textarea
                className="form-control form-control-sm"
                rows={4}
                value={editedService.summaryContent || ''}
                onChange={(e) => updateField('summaryContent', e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">Summary 圖片URL (summaryImageUrl)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.summaryImageUrl || ''}
                onChange={(e) => updateField('summaryImageUrl', e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">Summary 圖片替代文字 (summaryImageAlt)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedService.summaryImageAlt || ''}
                onChange={(e) => updateField('summaryImageAlt', e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label className="form-label small">Summary 側邊文字 (summarySideText)</label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                value={editedService.summarySideText || ''}
                onChange={(e) => updateField('summarySideText', e.target.value)}
              />
            </div>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="w-100">
            <div className="mb-2">
              <label className="form-label small">特性列表 (Features)</label>
              {(Array.isArray(editedService.features) ? editedService.features : []).map((feature, index) => (
                <div key={index} className="d-flex gap-2 mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={feature || ''}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    placeholder="輸入特性"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeFeature(index)}
                  >
                    刪除
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={addFeature}
              >
                + 新增特性
              </button>
            </div>
          </div>
        )}

        {activeTab === 'processes' && (
          <div className="w-100">
            {/* Eligibility Process */}
            {editedService.processes?.eligibility && (
              <div className="mb-4">
                <h6 className="mb-2">申請資格 (Eligibility)</h6>
                <div className="mb-2">
                  <label className="form-label small">標題</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedService.processes.eligibility.title || ''}
                    onChange={(e) => updateProcessField('eligibility', 'title', e.target.value)}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small">數據項目</label>
                  {(Array.isArray(editedService.processes.eligibility.data) ? editedService.processes.eligibility.data : []).map((item, index) => (
                    <div key={index} className="border rounded p-2 mb-2">
                      <div className="mb-2">
                        <label className="form-label small">標題</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={item.title || ''}
                          onChange={(e) => updateProcessDataItem('eligibility', index, 'title', e.target.value)}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label small">描述</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={item.description || ''}
                          onChange={(e) => updateProcessDataItem('eligibility', index, 'description', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeProcessDataItem('eligibility', index)}
                      >
                        刪除項目
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => addProcessDataItem('eligibility')}
                  >
                    + 新增項目
                  </button>
                </div>
              </div>
            )}

            {/* Documents Process (only for crossBorder) */}
            {editedService.serviceType === 'crossBorder' && editedService.processes?.documents && (
              <div className="mb-4">
                <h6 className="mb-2">預備文件 (Documents)</h6>
                <div className="mb-2">
                  <label className="form-label small">標題</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedService.processes.documents.title || ''}
                    onChange={(e) => updateProcessField('documents', 'title', e.target.value)}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small">數據項目</label>
                  {(Array.isArray(editedService.processes.documents.data) ? editedService.processes.documents.data : []).map((item, index) => (
                    <div key={index} className="border rounded p-2 mb-2">
                      <div className="mb-2">
                        <label className="form-label small">標題</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={item.title || ''}
                          onChange={(e) => updateProcessDataItem('documents', index, 'title', e.target.value)}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label small">描述</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={item.description || ''}
                          onChange={(e) => updateProcessDataItem('documents', index, 'description', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeProcessDataItem('documents', index)}
                      >
                        刪除項目
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => addProcessDataItem('documents')}
                  >
                    + 新增項目
                  </button>
                </div>
              </div>
            )}

            {/* Process Process */}
            {editedService.processes?.process && (
              <div className="mb-4">
                <h6 className="mb-2">申請流程 (Process)</h6>
                <div className="mb-2">
                  <label className="form-label small">標題</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedService.processes.process.title || ''}
                    onChange={(e) => updateProcessField('process', 'title', e.target.value)}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small">數據項目</label>
                  {(Array.isArray(editedService.processes.process.data) ? editedService.processes.process.data : []).map((item, index) => (
                    <div key={index} className="border rounded p-2 mb-2">
                      <div className="mb-2">
                        <label className="form-label small">標題</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={item.title || ''}
                          onChange={(e) => updateProcessDataItem('process', index, 'title', e.target.value)}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label small">描述</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={item.description || ''}
                          onChange={(e) => updateProcessDataItem('process', index, 'description', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeProcessDataItem('process', index)}
                      >
                        刪除項目
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => addProcessDataItem('process')}
                  >
                    + 新增項目
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
          {hasChanges && <div className="text-success small me-2">有未儲存的變更</div>}
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            儲存變更
          </button>
        </div>
      </div>
    </div>
  );
};

// Main ServiceTypeDataEditor Component
const ServiceTypeDataEditor = ({ filename }) => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState('');
  const [activeTab, setActiveTab] = useState('info'); // 'info' or 'services'

  const refreshServiceTypes = async () => {
    try {
      const data = await serviceTypeService.getListData();
      setServiceTypes(Array.isArray(data) ? data : []);
      if (data && data.length > 0 && !selectedServiceTypeId) {
        setSelectedServiceTypeId(String(data[0].id));
      }
    } catch (error) {
      console.error('Failed to load service types:', error);
      setServiceTypes([]);
    }
  };

  const refreshServices = async () => {
    try {
      const data = await serviceService.getListData();
      setServices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load services:', error);
      setServices([]);
    }
  };

  useEffect(() => {
    refreshServiceTypes();
    refreshServices();
  }, []);

  const selectedServiceType = useMemo(() => {
    if (!selectedServiceTypeId) return null;
    return serviceTypes.find(st => String(st.id) === String(selectedServiceTypeId)) || null;
  }, [selectedServiceTypeId, serviceTypes]);

  const handleServiceTypeUpdate = async (updated) => {
    if (!selectedServiceTypeId) return;
    try {
      await serviceTypeService.updateListObjectDataById(selectedServiceTypeId, updated);
      await refreshServiceTypes();
    } catch (error) {
      console.error('Failed to update service type:', error);
      alert('更新服務類型失敗');
    }
  };

  const [editedServiceType, setEditedServiceType] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (selectedServiceType) {
      setEditedServiceType(deepClone(selectedServiceType));
      setHasChanges(false);
    }
  }, [selectedServiceType]);

  useEffect(() => {
    if (editedServiceType && selectedServiceType) {
      const changed = JSON.stringify(editedServiceType) !== JSON.stringify(selectedServiceType);
      setHasChanges(changed);
    }
  }, [editedServiceType, selectedServiceType]);

  const updateServiceTypeField = (field, value) => {
    setEditedServiceType(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (path, value) => {
    setEditedServiceType(prev => {
      const newObj = deepClone(prev);
      const keys = path.split('.');
      let current = newObj;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!isObject(current[keys[i]])) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newObj;
    });
  };

  const handleSave = async () => {
    if (!editedServiceType) return;
    await handleServiceTypeUpdate(editedServiceType);
  };

  if (serviceTypes.length === 0) {
    return <div>載入中...</div>;
  }

  return (
    <div>
      <div className="mb-3">
        <label className="form-label">選擇服務類型以編輯：</label>
        <select
          className="form-select form-select-sm"
          value={selectedServiceTypeId}
          onChange={(e) => setSelectedServiceTypeId(e.target.value)}
        >
          {serviceTypes.map(st => (
            <option key={st.id} value={st.id}>
              {st.id} – {st.label || ''}
            </option>
          ))}
        </select>
      </div>

      {selectedServiceType && editedServiceType && (
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <h5 className="mb-3">{selectedServiceType.id} ({selectedServiceType.label})</h5>

            {/* Tab Navigation */}
            <ul className="nav nav-tabs mb-3" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'info' ? 'active' : ''}`}
                  onClick={() => setActiveTab('info')}
                  type="button"
                >
                  資訊
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'services' ? 'active' : ''}`}
                  onClick={() => setActiveTab('services')}
                  type="button"
                >
                  服務列表
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            {activeTab === 'info' && (
              <div className="w-100">
                <div className="mb-3">
                  <label className="form-label">ID</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedServiceType.id || ''}
                    readOnly
                    disabled
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">標籤 (Label)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedServiceType.label || ''}
                    onChange={(e) => updateServiceTypeField('label', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">URL路徑 (toURL)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedServiceType.toURL || ''}
                    onChange={(e) => updateServiceTypeField('toURL', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">標題 (Title)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedServiceType.title || ''}
                    onChange={(e) => updateServiceTypeField('title', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">副標題 (Subtitle)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedServiceType.subtitle || ''}
                    onChange={(e) => updateServiceTypeField('subtitle', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">圖片URL (ImageUrl)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editedServiceType.imageUrl || ''}
                    onChange={(e) => updateServiceTypeField('imageUrl', e.target.value)}
                  />
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                  {hasChanges && <div className="text-success small me-2">有未儲存的變更</div>}
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleSave}
                    disabled={!hasChanges}
                  >
                    儲存變更
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'services' && (
              <ServiceListEditor
                serviceTypeId={selectedServiceTypeId}
                services={services}
                onServicesChange={refreshServices}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceTypeDataEditor;

